/**
 * Catalog view config: column presets + picker visibility and the
 * low-stock row highlight. Pure, dependency-free helpers (plus a thin
 * localStorage layer) so the logic is unit-testable without the app.
 *
 * Design (see openspec change `merge-stock-overview-into-catalog`):
 * - Column *visibility* is the primitive. Two presets (Pricing default,
 *   Stock) are named shortcuts that apply a curated visible-set; the
 *   column picker toggles individual columns; deviating → "custom".
 * - The low-stock highlight is a COLOUR rule only — it never changes any
 *   stock figure. Default baseline = crate size (pack_size); alternative
 *   = since last delivery. Row is low when `current / baseline < pct`.
 * - Persistence: the URL is authoritative; localStorage seeds a fresh
 *   visit; hardcoded defaults when neither is set.
 */

export type LowStockRule = "crate" | "since_delivery";
export type CatalogPreset = "pricing" | "stock" | "custom";

/** Picker-managed DATA columns in canonical render order. ``name`` (and
 *  the drag ``grip``) are always-on and not part of this set. */
export const DATA_COLUMN_ORDER: string[] = [
  "unit",
  "sell",
  "buy",
  "margin",
  "revenue",
  "profit",
  "expected", // = current Stock on hand (shared spine)
  "refilled",
  "sold",
  "counted",
  "mismatch", // shared spine
  "mismatch_pct",
  "doc",
  "last_refill",
];

/** Human labels for the column picker. */
export const DATA_COLUMN_LABELS: Record<string, string> = {
  unit: "Unit",
  sell: "Sell",
  buy: "Buy",
  margin: "Margin",
  revenue: "Revenue",
  profit: "Profit",
  expected: "Stock",
  refilled: "Refilled",
  sold: "Sold",
  counted: "Counted",
  mismatch: "Mismatch",
  mismatch_pct: "Mismatch %",
  doc: "Days cover",
  last_refill: "Last refill",
};

/** Pricing preset visible data columns (matches the current catalog table,
 *  which keeps a Profit column alongside the spec's pricing set). */
export const PRICING_DATA_COLS: string[] = [
  "unit",
  "sell",
  "buy",
  "margin",
  "revenue",
  "profit",
  "expected",
  "mismatch",
];

/** Stock preset visible data columns. */
export const STOCK_DATA_COLS: string[] = [
  "expected",
  "refilled",
  "sold",
  "counted",
  "mismatch",
  "mismatch_pct",
  "doc",
  "last_refill",
];

/** Columns that source data from the windowed stock fetch (so the
 *  catalog only needs `/api/catalog/stock` when one of these is visible
 *  or the highlight is active). `expected`/`mismatch`/`counted` ride on
 *  ChainRow already, so they are NOT in this set. */
export const STOCK_FETCH_COLUMNS: string[] = [
  "refilled",
  "sold",
  "mismatch_pct",
  "doc",
  "last_refill",
];

export interface CatalogViewConfig {
  preset: CatalogPreset;
  /** Explicit visible data columns when `preset === "custom"`; otherwise null. */
  cols: string[] | null;
  hlOn: boolean;
  hlRule: LowStockRule;
  /** Threshold percent, e.g. 25 means "< 25% of baseline → low". */
  hlPct: number;
}

export const DEFAULT_VIEW: CatalogViewConfig = {
  preset: "pricing",
  cols: null,
  hlOn: true,
  hlRule: "crate",
  hlPct: 25,
};

function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const s = new Set(a);
  return b.every((x) => s.has(x));
}

/** The visible data columns for a config, in canonical render order. */
export function resolveVisibleDataCols(cfg: CatalogViewConfig): string[] {
  let want: string[];
  if (cfg.preset === "pricing") want = PRICING_DATA_COLS;
  else if (cfg.preset === "stock") want = STOCK_DATA_COLS;
  else want = cfg.cols ?? PRICING_DATA_COLS;
  const set = new Set(want);
  return DATA_COLUMN_ORDER.filter((c) => set.has(c));
}

/** Classify an explicit visible-set as a known preset, else "custom". */
export function detectPreset(cols: string[]): CatalogPreset {
  if (sameSet(cols, PRICING_DATA_COLS)) return "pricing";
  if (sameSet(cols, STOCK_DATA_COLS)) return "stock";
  return "custom";
}

/** Apply a preset selection (resets any custom column picks). */
export function applyPreset(
  cfg: CatalogViewConfig,
  preset: "pricing" | "stock",
): CatalogViewConfig {
  return { ...cfg, preset, cols: null };
}

/** Toggle a single data column on/off, re-detecting the preset label. */
export function toggleColumn(
  cfg: CatalogViewConfig,
  col: string,
): CatalogViewConfig {
  const current = new Set(resolveVisibleDataCols(cfg));
  if (current.has(col)) current.delete(col);
  else current.add(col);
  const next = DATA_COLUMN_ORDER.filter((c) => current.has(c));
  const preset = detectPreset(next);
  return preset === "custom"
    ? { ...cfg, preset: "custom", cols: next }
    : { ...cfg, preset, cols: null };
}

/** Does the active view need the windowed stock fetch? True when the
 *  highlight is on (it may read the since-delivery baseline / current
 *  stock) or any fetch-only stock column is visible. */
export function needsStockFetch(cfg: CatalogViewConfig): boolean {
  if (cfg.hlOn) return true;
  const visible = new Set(resolveVisibleDataCols(cfg));
  return STOCK_FETCH_COLUMNS.some((c) => visible.has(c));
}

// ---------------------------------------------------------------------------
// Low-stock predicate (colour rule only — never mutates stock figures)
// ---------------------------------------------------------------------------

export interface LowStockInputs {
  currentStock: number;
  /** Crate/carton size (ArticleMetadata.pack_size); null = no convention. */
  packSize: number | null;
  /** Stock right after the last delivery; null = never delivered. */
  sinceDeliveryBaselineQty: number | null;
}

export function lowStockBaseline(
  rule: LowStockRule,
  i: LowStockInputs,
): number | null {
  return rule === "crate" ? i.packSize : i.sinceDeliveryBaselineQty;
}

/** A row is low when current / baseline < threshold, evaluated only for a
 *  positive baseline. Missing (null) or non-positive baseline → not low. */
export function isLowStock(cfg: CatalogViewConfig, i: LowStockInputs): boolean {
  if (!cfg.hlOn) return false;
  const baseline = lowStockBaseline(cfg.hlRule, i);
  if (baseline === null || baseline <= 0) return false;
  return i.currentStock / baseline < cfg.hlPct / 100;
}

// ---------------------------------------------------------------------------
// localStorage seed (URL stays authoritative; this only fills a fresh visit)
// ---------------------------------------------------------------------------

const LS_KEY = "catalog.view.v1";

function isRule(v: unknown): v is LowStockRule {
  return v === "crate" || v === "since_delivery";
}
function isPreset(v: unknown): v is CatalogPreset {
  return v === "pricing" || v === "stock" || v === "custom";
}

/** Read the stored view config, or null when nothing valid is stored. */
export function readStoredView(): CatalogViewConfig | null {
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw) as Partial<CatalogViewConfig>;
    const preset = isPreset(o.preset) ? o.preset : DEFAULT_VIEW.preset;
    const cols =
      preset === "custom" && Array.isArray(o.cols)
        ? o.cols.filter((c) => DATA_COLUMN_ORDER.includes(c))
        : null;
    const pct =
      typeof o.hlPct === "number" && o.hlPct > 0 && o.hlPct < 100
        ? Math.round(o.hlPct)
        : DEFAULT_VIEW.hlPct;
    return {
      preset,
      cols,
      hlOn: typeof o.hlOn === "boolean" ? o.hlOn : DEFAULT_VIEW.hlOn,
      hlRule: isRule(o.hlRule) ? o.hlRule : DEFAULT_VIEW.hlRule,
      hlPct: pct,
    };
  } catch {
    return null;
  }
}

export function writeStoredView(cfg: CatalogViewConfig): void {
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify(cfg));
  } catch {
    // localStorage unavailable / quota — non-fatal; the URL still carries state.
  }
}
